<template>
  <div ref="root" class="relative w-full h-10">
    <!-- Trigger — styled to match the row inputs -->
    <button
      ref="trigger"
      type="button"
      class="w-full h-full flex items-center justify-between gap-2 rounded border border-solid bg-white px-3 text-base text-gray-dark cursor-pointer transition-colors"
      :class="isOpen ? 'border-primary-focus' : 'border-gray-border hover:border-primary-focus'"
      @click="toggle"
    >
      <span class="flex items-center gap-1.5 min-w-0 truncate">
        <bot-icon v-if="triggerIconType === ICON_TYPE.BOT" class="w-4 h-4 flex-shrink-0"/>
        <template v-else-if="triggerIconType === ICON_TYPE.BOT_MARKET">
          <bot-icon class="w-4 h-4 flex-shrink-0"/>
          <vs-icon :icon="SCOPE_ICON.MARKET" size="1.1rem" class="flex-shrink-0 leading-none"/>
        </template>
        <vs-icon v-else :icon="triggerIconType" size="1.1rem" class="flex-shrink-0 leading-none"/>
        <span class="truncate" :title="triggerLabel">{{ triggerLabel }}</span>
      </span>
      <svg class="flex-shrink-0 w-4 h-4 text-primary transform transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        viewBox="0 0 20 20" fill="none"
      >
        <path d="M6 8l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    <!-- Menu — teleported to <body> so it is never clipped by the table's scroll container -->
    <div ref="menu" v-show="isOpen" class="variable-scope-picker-menu" :style="menuStyle">
      <!-- Card 1: the scope types (compact, never stretched) -->
      <ul class="w-56 py-1 rounded border border-solid border-gray-border bg-white shadow-lg">
        <li v-for="type in scopeTypes"
          :key="type.value"
          class="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer text-base transition-colors"
          :class="activePane === type.value ? 'bg-primary-light text-primary-focus font-medium' : 'text-gray-dark hover:bg-primary-light'"
          @mouseenter="activePane = type.value"
          @click="onScopeTypeClick(type.value)"
        >
          <span class="flex items-center gap-2 truncate">
            <bot-icon v-if="type.iconType === ICON_TYPE.BOT" class="w-4 h-4 flex-shrink-0"/>
            <template v-else-if="type.iconType === ICON_TYPE.BOT_MARKET">
              <bot-icon class="w-4 h-4 flex-shrink-0"/>
              <vs-icon :icon="SCOPE_ICON.MARKET" size="1.1rem" class="flex-shrink-0 leading-none"/>
            </template>
            <vs-icon v-else :icon="type.iconType" size="1.1rem" class="flex-shrink-0 leading-none"/>
            <span class="truncate">{{ type.label }}</span>
          </span>
          <svg v-if="type.value !== SCOPE.GLOBAL" class="flex-shrink-0 w-4 h-4 opacity-50" viewBox="0 0 20 20" fill="none">
            <path d="M8 6l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </li>
      </ul>

      <!-- Card 2: a SEPARATE adjacent card with the contextual list. Width fits its content
           (short market/bot names don't force a wide card), capped so long names don't overflow. -->
      <div v-if="activePane !== SCOPE.GLOBAL"
        class="absolute top-0 rounded border border-solid border-gray-border bg-white shadow-lg overflow-hidden"
        style="width: max-content; min-width: 8rem; max-width: 24rem"
        :class="flipLeft ? 'right-full mr-2' : 'left-full ml-2'"
      >
        <!-- Search (shown once the list gets long) -->
        <div v-if="showSearch" class="p-2 border-b border-solid border-soft">
          <input
            ref="search"
            v-model="query"
            type="text"
            :placeholder="activePane === SCOPE.BOT ? 'Search bots…' : 'Search markets…'"
            class="w-full h-8 rounded border border-solid border-gray-border px-2 text-sm focus:border-primary-focus outline-none"
            @click.stop
          />
        </div>

        <!-- Scrollable list (capped height) -->
        <div class="py-1 overflow-y-auto" style="max-height: 24rem">
          <!-- BOT -->
          <template v-if="activePane === SCOPE.BOT">
            <p v-if="!filteredBots.length" class="px-3 py-2 text-sm text-gray">
              {{ logicalBots.length ? 'No matches.' : 'No bots available.' }}
            </p>
            <button v-for="bot in filteredBots"
              :key="bot.value"
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-left text-base cursor-pointer bg-transparent border-0 appearance-none transition-colors"
              :class="isSelectedBot(bot.value) ? 'bg-primary-light text-primary-focus font-medium' : 'text-gray-dark hover:bg-primary-light'"
              @click="selectBot(bot)"
            >
              <bot-icon class="w-4 h-4 flex-shrink-0"/>
              <span class="truncate" :title="bot.text">{{ bot.text }}</span>
            </button>
          </template>

          <!-- MARKET (grouped by bot, hierarchy shown visually) -->
          <template v-else-if="activePane === SCOPE.BOT_MARKET">
            <p v-if="!filteredMarketGroups.length" class="px-3 py-2 text-sm text-gray leading-snug">
              {{ marketCount ? 'No matches.' : 'No multi-market bots available.' }}
            </p>
            <div v-for="group in filteredMarketGroups" :key="group.multiBotId" class="mb-1">
              <p class="flex items-center gap-1.5 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray">
                <bot-icon class="w-3.5 h-3.5 flex-shrink-0"/>
                <span class="truncate" :title="group.botName">{{ group.botName }}</span>
              </p>
              <button v-for="market in group.markets"
                :key="`${group.multiBotId}-${market.value}`"
                type="button"
                class="w-full flex items-center gap-2 pl-7 pr-3 py-2 text-left text-base cursor-pointer bg-transparent border-0 appearance-none transition-colors"
                :class="isSelectedMarket(group.multiBotId, market.value) ? 'bg-primary-light text-primary-focus font-medium' : 'text-gray-dark hover:bg-primary-light'"
                @click="selectMarket(group, market)"
              >
                <vs-icon :icon="SCOPE_ICON.MARKET" size="1.1rem" class="flex-shrink-0 leading-none"/>
                <span class="truncate" :title="market.text">{{ market.text }}</span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VARIABLE_SCOPE } from '@/store/constants'
import { SCOPE_ICON, SCOPE_ICON_TYPE } from '@/constants/variable-scope.constants'
import { getLogicalBotOptions, getMarketScopeGroups } from '@/utils/market.utils'
import BotIcon from '@/assets/images/icons/BotIcon.vue'

// Icon to render per scope level: globe = global, the amio bot glyph = bot, and bot glyph +
// storefront = bot+market.
const ICON_BY_SCOPE = {
  [VARIABLE_SCOPE.GLOBAL]: SCOPE_ICON.GLOBAL,
  [VARIABLE_SCOPE.BOT]: SCOPE_ICON_TYPE.BOT,
  [VARIABLE_SCOPE.BOT_MARKET]: SCOPE_ICON_TYPE.BOT_MARKET
}

const CARD_A_WIDTH = 224 // w-56
const CARD_B_WIDTH = 384 // matches the list card max-width (24rem)
const GAP = 8
const SEARCH_THRESHOLD = 10 // show a search box once a list reaches this many items

export default {
  name: 'VariableScopePicker',

  components: { BotIcon },

  props: {
    value: {
      type: Object,
      default: () => ({ scope: VARIABLE_SCOPE.GLOBAL })
    }
  },

  data() {
    return {
      SCOPE: VARIABLE_SCOPE,
      ICON_TYPE: SCOPE_ICON_TYPE,
      SCOPE_ICON,
      isOpen: false,
      activePane: this.value.scope || VARIABLE_SCOPE.GLOBAL,
      query: '',
      menuTop: 0,
      menuLeft: 0,
      flipLeft: false
    }
  },

  computed: {
    menuStyle() {
      return {
        position: 'fixed',
        top: `${this.menuTop}px`,
        left: `${this.menuLeft}px`,
        zIndex: 9999
      }
    },

    scopeTypes() {
      return [
        { value: VARIABLE_SCOPE.GLOBAL, label: 'Global', iconType: ICON_BY_SCOPE[VARIABLE_SCOPE.GLOBAL] },
        { value: VARIABLE_SCOPE.BOT, label: 'Bot', iconType: ICON_BY_SCOPE[VARIABLE_SCOPE.BOT] },
        { value: VARIABLE_SCOPE.BOT_MARKET, label: 'Bot + Market', iconType: ICON_BY_SCOPE[VARIABLE_SCOPE.BOT_MARKET] }
      ]
    },

    logicalBots() {
      return getLogicalBotOptions()
    },

    marketGroups() {
      return getMarketScopeGroups()
    },

    marketCount() {
      return this.marketGroups.reduce((total, group) => total + group.markets.length, 0)
    },

    normalizedQuery() {
      return this.query.trim().toLowerCase()
    },

    filteredBots() {
      if (!this.normalizedQuery) return this.logicalBots
      return this.logicalBots.filter(bot => bot.text.toLowerCase().includes(this.normalizedQuery))
    },

    filteredMarketGroups() {
      if (!this.normalizedQuery) return this.marketGroups
      return this.marketGroups
        .map(group => ({
          ...group,
          markets: group.markets.filter(market =>
            market.text.toLowerCase().includes(this.normalizedQuery) ||
            group.botName.toLowerCase().includes(this.normalizedQuery)
          )
        }))
        .filter(group => group.markets.length > 0)
    },

    showSearch() {
      if (this.activePane === VARIABLE_SCOPE.BOT) return this.logicalBots.length >= SEARCH_THRESHOLD
      if (this.activePane === VARIABLE_SCOPE.BOT_MARKET) return this.marketCount >= SEARCH_THRESHOLD
      return false
    },

    triggerIconType() {
      return ICON_BY_SCOPE[this.value.scope] || ICON_BY_SCOPE[VARIABLE_SCOPE.GLOBAL]
    },

    triggerLabel() {
      if (this.value.scope === VARIABLE_SCOPE.BOT) {
        const bot = this.logicalBots.find(b => b.value === this.value.scope_value)
        return bot ? bot.text : 'Select bot'
      }
      if (this.value.scope === VARIABLE_SCOPE.BOT_MARKET) {
        const group = this.marketGroups.find(g => g.multiBotId === this.value.scope_value)
        const market = group && group.markets.find(m => m.value === this.value.market_id)
        if (group && market) return `${group.botName} · ${market.text}`
        return 'Select market'
      }
      return 'Global'
    }
  },

  watch: {
    value: {
      handler(val) {
        this.activePane = (val && val.scope) || VARIABLE_SCOPE.GLOBAL
      }
    },
    activePane() {
      // reset the filter when switching panes and focus the search box if it appears
      this.query = ''
      this.$nextTick(() => {
        if (this.showSearch && this.$refs.search) this.$refs.search.focus()
      })
    }
  },

  mounted() {
    // Teleport the menu to <body> so the table's overflow/scroll container can't clip it.
    if (this.$refs.menu) document.body.appendChild(this.$refs.menu)
    document.addEventListener('mousedown', this.onDocumentMousedown)
    document.addEventListener('keydown', this.onKeydown)
  },

  beforeDestroy() {
    if (this.$refs.menu && this.$refs.menu.parentNode) this.$refs.menu.parentNode.removeChild(this.$refs.menu)
    document.removeEventListener('mousedown', this.onDocumentMousedown)
    document.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('scroll', this.onWindowChange, true)
    window.removeEventListener('resize', this.onWindowChange)
  },

  methods: {
    toggle() {
      this.isOpen ? this.close() : this.open()
    },

    open() {
      this.isOpen = true
      this.query = ''
      this.activePane = this.value.scope || VARIABLE_SCOPE.GLOBAL
      this.$nextTick(this.updatePosition)
      window.addEventListener('scroll', this.onWindowChange, true)
      window.addEventListener('resize', this.onWindowChange)
    },

    close() {
      this.isOpen = false
      window.removeEventListener('scroll', this.onWindowChange, true)
      window.removeEventListener('resize', this.onWindowChange)
    },

    onWindowChange(event) {
      // Ignore scrolls that happen INSIDE the menu (the list scrolling) — only the page
      // scrolling/resizing makes the fixed coordinates stale, so only then do we close.
      if (event && event.target && this.$refs.menu && this.$refs.menu.contains(event.target)) return
      this.close()
    },

    updatePosition() {
      const trigger = this.$refs.trigger
      if (!trigger || !trigger.getBoundingClientRect) return
      const rect = trigger.getBoundingClientRect()
      const viewportWidth = window.innerWidth || 1024

      this.menuTop = rect.bottom + 4

      // Left-align card 1 to the trigger, clamped so it never overflows the viewport.
      let left = rect.left
      if (left + CARD_A_WIDTH > viewportWidth - GAP) left = viewportWidth - CARD_A_WIDTH - GAP
      if (left < GAP) left = GAP
      this.menuLeft = left

      // Card 2 expands to the right; flip to the left only if there isn't room.
      this.flipLeft = (left + CARD_A_WIDTH + GAP + CARD_B_WIDTH) > viewportWidth
    },

    onScopeTypeClick(type) {
      this.activePane = type
      // Global is a leaf — selecting it completes the choice. Bot/Market reveal their list.
      if (type === VARIABLE_SCOPE.GLOBAL) this.selectGlobal()
    },

    selectGlobal() {
      this.emitChange({ scope: VARIABLE_SCOPE.GLOBAL })
      this.close()
    },

    selectBot(bot) {
      this.emitChange({ scope: VARIABLE_SCOPE.BOT, scope_value: bot.value, is_multi: !!bot.isMulti })
      this.close()
    },

    selectMarket(group, market) {
      this.emitChange({
        scope: VARIABLE_SCOPE.BOT_MARKET,
        scope_value: group.multiBotId,
        market_id: market.value
      })
      this.close()
    },

    emitChange(payload) {
      this.$emit('input', payload)
    },

    isSelectedBot(botValue) {
      return this.value.scope === VARIABLE_SCOPE.BOT && this.value.scope_value === botValue
    },

    isSelectedMarket(multiBotId, marketId) {
      return this.value.scope === VARIABLE_SCOPE.BOT_MARKET &&
        this.value.scope_value === multiBotId &&
        this.value.market_id === marketId
    },

    onDocumentMousedown(event) {
      if (!this.isOpen) return
      const inRoot = this.$refs.root && this.$refs.root.contains(event.target)
      const inMenu = this.$refs.menu && this.$refs.menu.contains(event.target)
      if (!inRoot && !inMenu) this.close()
    },

    onKeydown(event) {
      if (event.key === 'Escape') this.close()
    }
  }
}
</script>
